import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Lock, Eye, EyeOff, Mail, Briefcase, Building2, UserCircle, CheckSquare, Square, ArrowLeft, ArrowRight, Key, Copy, Clock, Loader2, User, ScanFace, AlertTriangle, CheckCircle2, ShieldCheck } from "lucide-react";
import secureflexLogo from "@/assets/secureflex-logo-new.png";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, INTENDED_ROLE_KEY } from "@/contexts/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import { useFaceVerification } from "@/hooks/useFaceVerification";
import FaceIDScanner from "@/components/app/FaceIDScanner";
import { validateEmail, isSafeInput } from "@/lib/validation";

type Role = "officer" | "site-owner" | "business-owner" | "admin";
type AuthMode = "login" | "signup" | "forgot" | "shift-password";
type Step = "select-role" | "auth";

const REMEMBER_KEY = "secureflex_remembered_email";

const roleCards: { id: Role; label: string; description: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "officer", label: "Officer", description: "Check in at checkpoints, report incidents, and manage your patrol schedule", icon: Shield },
  { id: "site-owner", label: "Site Operator", description: "Monitor your sites, view officer activity, coverage reports and incidents", icon: Building2 },
  { id: "business-owner", label: "Security Operator", description: "Full control over officers, sites, schedules, billing and operations", icon: Briefcase },
  { id: "admin", label: "Admin", description: "Platform administration, analytics, user management and client support", icon: ShieldCheck },
];

const AppLogin = () => {
  const navigate = useNavigate();
  const { user, role: userRole, roles: userRoles } = useAuth();
  const [step, setStep] = useState<Step>("select-role");
  const [role, setRole] = useState<Role>("officer");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>("login");
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem(REMEMBER_KEY));
  const [savedEmail] = useState(() => localStorage.getItem(REMEMBER_KEY) || "");

  // Redirect if already logged in — respect the intended role from role card selection
  useEffect(() => {
    if (user && userRole) {
      const roleRoutes: Record<string, string> = {
        officer: "/app/officer",
        site_owner: "/app/site-owner",
        business_owner: "/app/business-owner",
        admin: "/app/admin",
      };
      // Use the stored intended role if the user has it, otherwise use the context role
      const intendedDbRole = role === "site-owner" ? "site_owner" : role === "business-owner" ? "business_owner" : role;
      const storedIntended = localStorage.getItem(INTENDED_ROLE_KEY) as string | null;
      
      // If user has the intended role, use it. Otherwise fallback to their primary assigned role.
      const targetRole = (storedIntended && userRoles.includes(storedIntended as any)) ? storedIntended : 
                         (userRoles.includes(intendedDbRole as any) ? intendedDbRole : userRole);
      
      navigate(roleRoutes[targetRole] || roleRoutes[userRole] || "/app/business-owner", { replace: true });
    }
  }, [user, userRole, navigate]);

  const handleSelectRole = (selectedRole: Role) => {
    setRole(selectedRole);
    // Store the intended role so AuthContext and redirect respect it
    const dbRole = selectedRole === "site-owner" ? "site_owner" : selectedRole === "business-owner" ? "business_owner" : selectedRole;
    localStorage.setItem(INTENDED_ROLE_KEY, dbRole);
    if (selectedRole === "admin") setAuthMode("login");
    setStep("auth");
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const formPassword = formData.get("password") as string || "";
    
    let loginEmail: string;
    
    if (role === "officer") {
      // Officers log in with username + shift password via edge function
      const formUsername = (formData.get("username") as string || "").trim().toLowerCase();
      if (!formUsername || !formPassword) {
        toast.error("Please fill in all fields");
        return;
      }
      if (!isSafeInput(formUsername)) { toast.error("Invalid characters detected."); return; }
      
      setLoading(true);
      try {
        // Validate shift password and sync auth password server-side
        const { data: loginData, error: loginError } = await supabase.functions.invoke("officer-login", {
          body: { username: formUsername, password: formPassword.trim() },
        });
        
        if (loginError || loginData?.error) {
          console.error("officer-login error:", loginError, loginData);
          let msg = "Login failed. Check your username and shift password.";
          if (loginData?.error) {
            msg = loginData.error;
          } else if (loginError) {
            msg = loginError.message;
            try {
              const errorBody = await (loginError as any).context?.json();
              if (errorBody?.error) msg = errorBody.error;
              else if (errorBody?.message) msg = errorBody.message;
            } catch (e) {}

            if (msg === "Edge Function returned a non-2xx status code") {
              msg = "Officer login service error (Status " + (loginError as any).status + ")";
            }
          }
          toast.error(msg);
          setLoading(false);
          return;
        }
        
        loginEmail = loginData.email;
      } catch (err: any) {
        console.error("officer-login networking error:", err);
        toast.error("Network error: " + (err.message || "Please try again."));
        setLoading(false);
        return;
      }
    } else {
      const formEmail = (formData.get("email") as string || "").trim();
      if (!formEmail || !formPassword) {
        toast.error("Please fill in all fields");
        return;
      }
      if (!validateEmail(formEmail)) { toast.error("Please enter a valid email address"); return; }
      if (!isSafeInput(formEmail)) { toast.error("Invalid characters detected."); return; }
      loginEmail = formEmail;
      setLoading(true);
    }

    try {
      const { error: loginError } = await supabase.auth.signInWithPassword({ email: loginEmail, password: formPassword });
      setLoading(false);
      
      if (loginError) {
        console.error("Auth sign-in error:", loginError);
        if (loginError.message === "Email not confirmed") {
          toast.error("Please verify your email address before signing in.");
        } else if (loginError.message === "Invalid login credentials") {
          if (role === "officer") {
            toast.error("Authentication failed. The shift password may have expired or not synced. Please try retrieving it again.");
          } else {
            toast.error("Invalid credentials. Please check your email and password.");
          }
        } else {
          toast.error(loginError.message || "Sign in failed");
        }
        return;
      }
      
      if (rememberMe && role !== "officer") {
        localStorage.setItem(REMEMBER_KEY, loginEmail);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
      
      toast.success("Signed in successfully");

      // Proactive redirection: Calculate target and navigate immediately
      // This supplements the useEffect and provides a faster response
      const roleRoutes: Record<string, string> = {
        officer: "/app/officer",
        site_owner: "/app/site-owner",
        business_owner: "/app/business-owner",
        admin: "/app/admin",
      };
      
      setTimeout(() => {
        const dbRole = role === "site-owner" ? "site_owner" : role === "business-owner" ? "business_owner" : role;
        navigate(roleRoutes[dbRole] || "/app/business-owner", { replace: true });
      }, 500);
      
    } catch (err: any) {
      console.error("Critical authentication failure:", err);
      setLoading(false);
      toast.error(err?.message || "A network or server error occurred. Please try again.");
    }
  };

  const handleSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const formEmail = (formData.get("email") as string || "").trim();
    const formPassword = formData.get("password") as string || "";
    const formFullName = (formData.get("fullName") as string || "").trim();
    if (!formEmail || !formPassword || !formFullName) {
      toast.error("Please fill in all fields");
      return;
    }
    if (!validateEmail(formEmail)) { toast.error("Please enter a valid email address"); return; }
    if (!isSafeInput(formFullName)) { toast.error("Invalid characters detected in name"); return; }
    if (formPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    const dbRole = role === "site-owner" ? "site_owner" : role === "business-owner" ? "business_owner" : role === "admin" ? "admin" : "officer";

    try {
      const { error } = await supabase.auth.signUp({
        email: formEmail,
        password: formPassword,
        options: {
          emailRedirectTo: `${window.location.origin}/app`,
          data: { full_name: formFullName, role: dbRole },
        },
      });
      setLoading(false);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success("Account created! You can now sign in.", { duration: 5000 });
      setAuthMode("login");
    } catch (err: any) {
      console.error("Critical signup failure:", err);
      setLoading(false);
      toast.error(err?.message || "A network error occurred. Please try again.");
    }
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const formEmail = (formData.get("email") as string || "").trim();
    if (!formEmail) {
      toast.error("Please enter your email");
      return;
    }
    if (!validateEmail(formEmail)) { toast.error("Please enter a valid email address"); return; }
    setLoading(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(formEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setLoading(false);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success("Password reset email sent! Check your inbox.");
      setAuthMode("login");
    } catch (err: any) {
      console.error("Critical reset failure:", err);
      setLoading(false);
      toast.error(err?.message || "A network error occurred. Please try again.");
    }
  };

  // Shift password lookup with face verification
  const [shiftLoading, setShiftLoading] = useState(false);
  const [shiftResult, setShiftResult] = useState<{ officerName: string; password: string; expiresAt: string } | null>(null);
  const [shiftError, setShiftError] = useState<string | null>(null);
  const [shiftStep, setShiftStep] = useState<"username" | "verifying" | "result" | "denied">("username");
   const [officerLookup, setOfficerLookup] = useState<{ officerId: string; officerName: string; facePhotoUrl: string | null; siteId: string | null; username: string } | null>(null);
   const [showFlash, setShowFlash] = useState(false);
   const shiftVideoRef = useRef<HTMLVideoElement>(null);

  const { 
    startCamera: shiftStartCamera, 
    stopCamera: shiftStopCamera, 
    verifyFace: shiftVerifyFace, 
    isVerifying: shiftIsVerifying,
    scanningState: shiftScanningState,
    error: shiftFaceError
  } = useFaceVerification();

  const startVerification = async (lookup: { officerId: string; officerName: string; facePhotoUrl: string | null; siteId: string | null; username: string }) => {
    setShiftStep("verifying");
    setShiftError(null);
    
    // Slight delay to ensure video ref is ready
    setTimeout(async () => {
      const video = shiftVideoRef.current;
      if (!video) {
        setShiftStep("denied");
        setShiftError("Camera element not available.");
        return;
      }
      const cameraOk = await shiftStartCamera(video);
      if (!cameraOk) {
        setShiftStep("denied");
        // We set the detailed error from the hook, or a generic one if missing
        setShiftError(shiftFaceError || "Camera access denied."); 
        return;
      }
      
      try {
        const result = await shiftVerifyFace(lookup.facePhotoUrl!, {
          officerId: lookup.officerId,
          officerName: lookup.officerName,
          siteId: lookup.siteId,
        });
        
        // Wait 2s to show success/error result in the scanner loop
        setTimeout(() => shiftStopCamera(), 2000);

        if (result.match && result.liveness && result.confidence >= 70) {
          const { data: pwdData, error: pwdError } = await supabase.functions.invoke("get-shift-password", {
            body: { username: lookup.username },
          });
            if (pwdError || pwdData?.error) {
              console.error("get-shift-password error:", pwdError, pwdData);
              let msg = "No active shift password.";
              if (pwdData?.error) {
                msg = pwdData.error;
              } else if (pwdError) {
                msg = pwdError.message;
                try {
                  const errorBody = await (pwdError as any).context?.json();
                  if (errorBody?.error) msg = errorBody.error;
                  else if (errorBody?.message) msg = errorBody.message;
                } catch (e) {}

                if (msg === "Edge Function returned a non-2xx status code") {
                  msg = "Password service error (Status " + (pwdError as any).status + ")";
                }
              }
              setShiftStep("denied");
              setShiftError(msg);
              return;
            }
          setShiftResult(pwdData);
          setShiftStep("result");
          toast.success(`Identity verified (${result.confidence}%)`);
        } else {
          setTimeout(() => setShiftStep("denied"), 2000);
          setShiftError(result.reason || "Face verification failed.");
        }
      } catch (err) {
        shiftStopCamera();
        setShiftStep("denied");
        setShiftError("Face verification error.");
      }
    }, 100);
  };

  const handleShiftPasswordLookup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const formUsername = (formData.get("username") as string || "").trim();
    if (!formUsername) {
      toast.error("Please enter your username");
      return;
    }
    if (!isSafeInput(formUsername)) { toast.error("Invalid characters detected"); return; }
    setShiftLoading(true);
    setShiftResult(null);
    setShiftError(null);
    try {
      const { data: lookupData, error: lookupError } = await supabase.functions.invoke("lookup-officer", {
        body: { username: formUsername },
      });
      if (lookupError || lookupData?.error) {
        console.error("lookup-officer error:", lookupError, lookupData);
        let msg = "No officer found with this username";
        if (lookupData?.error) {
          msg = lookupData.error;
        } else if (lookupError) {
          msg = lookupError.message;
          try {
            const errorBody = await (lookupError as any).context?.json();
            if (errorBody?.error) msg = errorBody.error;
            else if (errorBody?.message) msg = errorBody.message;
          } catch (e) {}
          
          if (msg === "Edge Function returned a non-2xx status code") {
            msg = "Officer lookup service error (Status " + (lookupError as any).status + ")";
          }
        }
        setShiftError(msg);
        setShiftLoading(false);
        return;
      }
      if (!lookupData.facePhotoUrl) {
        setShiftError("No reference photo on file. Contact your supervisor to set up face verification.");
        setShiftLoading(false);
        return;
      }
      const lookup = { ...lookupData, username: formUsername };
      setOfficerLookup(lookup);
      setShiftLoading(false);
      startVerification(lookup);
    } catch {
      setShiftError("Network error.");
      setShiftLoading(false);
    }
  };

  const handleRetryVerification = () => {
    if (officerLookup) {
      startVerification(officerLookup);
    } else {
      setShiftStep("username");
    }
  };

  const resetShiftFlow = () => {
    setShiftStep("username");
    setShiftResult(null);
    setShiftError(null);
    setOfficerLookup(null);
    setShowFlash(false);
    shiftStopCamera();
  };

  const copyShiftPassword = () => {
    if (shiftResult?.password) {
      navigator.clipboard.writeText(shiftResult.password);
      toast.success("Password copied to clipboard");
    }
  };

  if (user && userRole) return null;

  const selectedRoleCard = roleCards.find(r => r.id === role)!;
  const SelectedIcon = selectedRoleCard.icon;

  return (
    <div className="min-h-screen app-bg flex items-center justify-center relative overflow-hidden font-outfit">
      {/* Animated grid */}
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: `linear-gradient(hsl(var(--app-border-subtle)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--app-border-subtle)) 1px, transparent 1px)`,
        backgroundSize: "60px 60px"
      }} />
      {/* Gold radial glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-20" 
        style={{ background: "radial-gradient(ellipse, hsl(var(--app-gold) / 0.3), transparent 70%)" }} />

      <div className="relative z-10 w-full max-w-[480px] px-4 sm:px-6">
        {/* Back to website */}
        <div className="flex items-center justify-between mb-8">
          <button 
            onClick={() => navigate("/")} 
            className="flex items-center gap-2 py-2 px-3 -ml-3 rounded-lg text-sm text-[hsl(var(--app-text-secondary))] hover:text-[hsl(var(--app-text-primary))] hover:bg-white/5 transition-all"
          >
            <ArrowLeft className="w-4 h-4" /> <span className="hidden xs:inline">Back to website</span><span className="xs:hidden">Back</span>
          </button>
          <div className="flex items-center gap-3">
            <ThemeToggle />
          </div>
        </div>

        {/* Logo */}
        <div className="text-center mb-10">
          <img src={secureflexLogo} alt="SecureFlex Logo" className="h-20 w-20 sm:h-24 sm:w-24 object-contain mx-auto mb-4 drop-shadow-[0_0_15px_hsl(var(--app-gold)/0.4)]" />
          <h1 className="font-bebas text-4xl sm:text-5xl tracking-widest text-[hsl(var(--app-text-primary))]">SECUREFLEX</h1>
          <div className="h-px w-12 bg-[hsl(var(--app-gold))] mx-auto mt-2 opacity-50" />
          <p className="text-[10px] tracking-[0.3em] uppercase text-[hsl(var(--app-gold))] mt-3 font-outfit font-bold">Proactive Digital Fortress</p>
        </div>

        {step === "select-role" ? (
          <div className="animate-fade-in">
            <div className="app-card rounded-2xl overflow-hidden" style={{ boxShadow: "0 40px 80px rgba(0,0,0,0.5)" }}>
              <div className="h-px" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--app-gold)), transparent)" }} />
              <div className="p-8">
                <h2 className="font-bebas text-xl text-[hsl(var(--app-text-primary))] text-center mb-1">SELECT YOUR DASHBOARD</h2>
                <p className="text-xs text-[hsl(var(--app-text-muted))] text-center mb-6">Choose how you'll be using SecureFlex</p>
                
                <div className="space-y-3">
                  {roleCards.map((r) => {
                    const Icon = r.icon;
                    const isAdmin = r.id === "admin";
                    return (
                      <button
                        key={r.id}
                        onClick={() => handleSelectRole(r.id)}
                        className={`w-full p-4 rounded-xl border transition-all text-left group flex items-start gap-4 ${
                          isAdmin
                            ? "border-[hsl(var(--app-red))]/30 bg-[hsl(var(--app-red))]/5 hover:border-[hsl(var(--app-red))] hover:bg-[hsl(var(--app-red))]/10"
                            : "border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] hover:border-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.05)]"
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-lg border flex items-center justify-center flex-shrink-0 transition-all ${
                          isAdmin
                            ? "border-[hsl(var(--app-red))]/40 bg-[hsl(var(--app-red))]/10 group-hover:border-[hsl(var(--app-red))] group-hover:bg-[hsl(var(--app-red))]/20"
                            : "border-[hsl(var(--app-border-active))] bg-[hsl(var(--app-surface-2))] group-hover:border-[hsl(var(--app-gold))] group-hover:bg-[hsl(var(--app-gold)/0.1)]"
                        }`}>
                          <Icon className={`w-5 h-5 transition-colors ${
                            isAdmin
                              ? "text-[hsl(var(--app-red))]"
                              : "text-[hsl(var(--app-text-muted))] group-hover:text-[hsl(var(--app-gold))]"
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-semibold transition-colors ${
                                isAdmin
                                  ? "text-[hsl(var(--app-red))] group-hover:text-[hsl(var(--app-red))]"
                                  : "text-[hsl(var(--app-text-primary))] group-hover:text-[hsl(var(--app-gold))]"
                              }`}>{r.label}</p>
                              {isAdmin && (
                                <span className="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-widest bg-[hsl(var(--app-red))]/20 text-[hsl(var(--app-red))] border border-[hsl(var(--app-red))]/30">
                                  Restricted
                                </span>
                              )}
                            </div>
                            <ArrowRight className={`w-4 h-4 transition-colors opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 ${
                              isAdmin ? "text-[hsl(var(--app-red))]" : "text-[hsl(var(--app-text-muted))] group-hover:text-[hsl(var(--app-gold))]"
                            }`} />
                          </div>
                          <p className="text-xs text-[hsl(var(--app-text-muted))] mt-0.5 leading-relaxed">{r.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="animate-fade-in">
            <div className="app-card rounded-2xl overflow-hidden" style={{ boxShadow: "0 40px 80px rgba(0,0,0,0.5)" }}>
              <div className="h-px" style={{ background: "linear-gradient(90deg, transparent, hsl(var(--app-gold)), transparent)" }} />
              
              <div className="p-8">
                {/* Back + selected role */}
                <button
                  onClick={() => { setStep("select-role"); setAuthMode("login"); }}
                  className="flex items-center gap-1.5 text-xs text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-gold))] transition-colors mb-5"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Change dashboard
                </button>

                {/* Selected role badge */}
                <div className="flex items-center justify-center gap-2 mb-5">
                  <div className="px-3 py-1.5 rounded-full border border-[hsl(var(--app-gold)/0.3)] bg-[hsl(var(--app-gold)/0.08)] inline-flex items-center gap-2">
                    <SelectedIcon className="w-4 h-4 text-[hsl(var(--app-gold))]" />
                    <span className="text-xs font-semibold text-[hsl(var(--app-gold))]">{selectedRoleCard.label}</span>
                  </div>
                </div>

                {authMode === "shift-password" ? (
                  <>
                    <h2 className="font-bebas text-xl text-[hsl(var(--app-text-primary))] text-center mb-1">SHIFT PASSWORD</h2>
                    <p className="text-xs text-[hsl(var(--app-text-muted))] text-center mb-4">Face verification required to view your shift password</p>
                    
                    {shiftStep === "username" && (
                      <form onSubmit={handleShiftPasswordLookup} className="space-y-4">
                        <InputField icon={<User className="w-4 h-4" />} placeholder="Your username" name="username" autoComplete="username" />
                        <button type="submit" disabled={shiftLoading} className="w-full py-4 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-outfit font-bold text-base hover:brightness-110 hover:-translate-y-0.5 transition-all gold-glow disabled:opacity-50 flex items-center justify-center gap-2">
                          {shiftLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Looking up...</> : <><ScanFace className="w-4 h-4" /> Verify &amp; Get Password</>}
                        </button>
                        {shiftError && (
                          <p className="text-xs text-center text-red-400">{shiftError}</p>
                        )}
                      </form>
                    )}

                    {shiftStep === "verifying" && (
                      <div className="space-y-4 text-center">
                        <p className="text-sm font-medium text-[hsl(var(--app-text-primary))]">
                          {officerLookup?.officerName}, verify your identity
                        </p>
                        <FaceIDScanner 
                          videoRef={shiftVideoRef} 
                          state={shiftScanningState} 
                          error={shiftFaceError} 
                        />
                        <button
                          onClick={resetShiftFlow}
                          className="mt-2 text-xs text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-gold))] transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    )}

                    {shiftStep === "result" && shiftResult && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <CheckCircle2 className="w-5 h-5 text-[hsl(var(--app-green,142 71% 45%))]" />
                          <span className="text-xs font-semibold text-[hsl(var(--app-green,142 71% 45%))] uppercase tracking-wider">Identity Verified</span>
                        </div>
                        <div className="p-4 rounded-xl border border-[hsl(var(--app-gold)/0.3)] bg-[hsl(var(--app-gold)/0.05)]">
                          <p className="text-xs text-[hsl(var(--app-text-muted))] mb-1">Officer: <span className="text-[hsl(var(--app-text-primary))] font-semibold">{shiftResult.officerName}</span></p>
                          <div className="flex items-center justify-between mt-3">
                            <span className="font-mono text-2xl font-bold text-[hsl(var(--app-gold))] tracking-[4px]">
                              {shiftResult.password}
                            </span>
                            <button onClick={copyShiftPassword} className="p-2 rounded-lg border border-[hsl(var(--app-border-subtle))] hover:border-[hsl(var(--app-gold))] transition-colors">
                              <Copy className="w-4 h-4 text-[hsl(var(--app-text-secondary))]" />
                            </button>
                          </div>
                          <div className="flex items-center gap-1 mt-3 text-xs text-[hsl(var(--app-text-muted))]">
                            <Clock className="w-3 h-3" />
                            <span>Expires: {new Date(shiftResult.expiresAt).toLocaleString()}</span>
                          </div>
                        </div>
                        <button 
                          onClick={resetShiftFlow}
                          className="w-full py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))] text-sm hover:border-[hsl(var(--app-gold))] transition-colors"
                        >
                          Check another username
                        </button>
                      </div>
                    )}

                    {shiftStep === "denied" && (
                      <div className="space-y-4 text-center">
                        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
                          <AlertTriangle className="w-8 h-8 text-red-400" />
                        </div>
                        <p className="text-sm font-medium text-[hsl(var(--app-text-primary))]">Verification Failed</p>
                        <p className="text-xs text-[hsl(var(--app-text-muted))]">{shiftError || "Could not verify your identity. Please try again."}</p>
                        <button
                          onClick={handleRetryVerification}
                          className="w-full py-3 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-bold text-sm hover:brightness-110 transition-all gold-glow"
                        >
                          Try Again
                        </button>
                        <button
                          onClick={resetShiftFlow}
                          className="w-full py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-secondary))] text-sm hover:border-[hsl(var(--app-gold))] transition-colors"
                        >
                          Use Different Username
                        </button>
                      </div>
                    )}
                    
                    <div className="mt-4 text-center">
                      <button onClick={() => { setAuthMode("login"); resetShiftFlow(); }} className="text-sm text-[hsl(var(--app-gold))] hover:underline">Back to Sign In</button>
                    </div>
                  </>
                ) : authMode === "forgot" ? (
                  <>
                    <h2 className="font-bebas text-xl text-[hsl(var(--app-text-primary))] text-center mb-4">RESET PASSWORD</h2>
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      <InputField icon={<Mail className="w-4 h-4" />} placeholder="Email" type="email" name="email" autoComplete="email" />
                      <button type="submit" disabled={loading} className="w-full py-4 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-outfit font-bold text-base hover:brightness-110 hover:-translate-y-0.5 transition-all gold-glow disabled:opacity-50">
                        {loading ? "Sending..." : "Send Reset Link"}
                      </button>
                    </form>
                    <div className="mt-4 text-center">
                      <button onClick={() => setAuthMode("login")} className="text-sm text-[hsl(var(--app-gold))] hover:underline">Back to Sign In</button>
                    </div>
                  </>
                ) : (
                  <>
                    <form onSubmit={authMode === "login" ? handleLogin : handleSignup} className="space-y-4">
                      {authMode === "signup" && (
                        <InputField icon={<UserCircle className="w-4 h-4" />} placeholder="Full Name" name="fullName" autoComplete="name" />
                      )}
                      {role === "officer" && authMode === "login" ? (
                        <InputField icon={<User className="w-4 h-4" />} placeholder="Username" name="username" autoComplete="username" />
                      ) : (
                        <InputField icon={<Mail className="w-4 h-4" />} placeholder="Email" type="email" name="email" defaultValue={savedEmail} autoComplete="email" />
                      )}
                      <InputField 
                        icon={<Lock className="w-4 h-4" />} 
                        placeholder="Password" 
                        type={showPassword ? "text" : "password"}
                        name="password"
                        autoComplete={authMode === "login" ? "current-password" : "new-password"}
                        suffix={
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))]">
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        }
                      />

                      {authMode === "login" && (
                        <div className="flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => setRememberMe(!rememberMe)}
                            className="flex items-center gap-2 text-xs text-[hsl(var(--app-text-secondary))] hover:text-[hsl(var(--app-text-primary))] transition-colors"
                          >
                            {rememberMe ? (
                              <CheckSquare className="w-4 h-4 text-[hsl(var(--app-gold))]" />
                            ) : (
                              <Square className="w-4 h-4" />
                            )}
                            Remember me
                          </button>
                          <button type="button" onClick={() => setAuthMode("forgot")} className="text-xs text-[hsl(var(--app-gold))] hover:underline">
                            Forgot password?
                          </button>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-4 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] font-outfit font-bold text-base hover:brightness-110 hover:-translate-y-0.5 transition-all gold-glow disabled:opacity-50"
                      >
                        {loading ? (authMode === "login" ? "Signing in..." : "Creating account...") : (authMode === "login" ? "Sign In" : "Create Account")}
                      </button>
                    </form>

                    {authMode === "signup" && (
                      <p className="mt-3 text-[10px] text-center text-[hsl(var(--app-text-muted))]">
                        A verification email will be sent to confirm your address
                      </p>
                    )}

                    {/* Shift password link for officers */}
                    {role === "officer" && authMode === "login" && (
                      <div className="mt-4 text-center">
                        <button onClick={() => setAuthMode("shift-password")} className="text-xs text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-gold))] transition-colors flex items-center gap-1.5 mx-auto">
                          <Key className="w-3.5 h-3.5" /> Check your shift password
                        </button>
                      </div>
                    )}

                    {role !== "officer" && role !== "admin" && (
                      <div className="mt-4 text-center">
                        {authMode === "login" ? (
                          <button onClick={() => setAuthMode("signup")} className="text-sm text-[hsl(var(--app-text-secondary))]">
                            Don't have an account? <span className="text-[hsl(var(--app-gold))] hover:underline">Sign up →</span>
                          </button>
                        ) : (
                          <button onClick={() => setAuthMode("login")} className="text-sm text-[hsl(var(--app-text-secondary))]">
                            Already have an account? <span className="text-[hsl(var(--app-gold))] hover:underline">Sign in →</span>
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const InputField = ({ icon, suffix, ...props }: { icon: React.ReactNode; suffix?: React.ReactNode } & React.InputHTMLAttributes<HTMLInputElement>) => (
  <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] focus-within:border-[hsl(var(--app-gold))] transition-colors">
    <span className="text-[hsl(var(--app-text-muted))]">{icon}</span>
    <input {...props} className="flex-1 bg-transparent text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none font-outfit" />
    {suffix}
  </div>
);

export default AppLogin;
